import React from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import { isBase64Image, getDateString } from '../../helpers/utils';
import { CredentialProofRequest } from '../../types/credentials';

interface ProofDetailsProps {
  proof: CredentialProofRequest;
}

const ProofDetails: React.FC<ProofDetailsProps> = ({ proof }) => {
  const mapKey = (key: string): string => {
    const mapped = key.charAt(0).toUpperCase() + key.slice(1);
    return mapped.replace(/_/g, ' ');
  };

  const createPredicateString = (p_name: string, p_type: string, p_value: number) => {
    if (p_name.toLowerCase().includes('date')) {
      const date = getDateString(p_value);
      switch (p_type) {
        case '>=':
          return p_name + ' is after or on ' + date;
        case '>':
          return p_name + ' is after ' + date;
        case '<=':
          return p_name + ' is before or on ' + date;
        case '<':
          return p_name + ' is before ' + date;
        case '=':
          return p_name + ' is on ' + date;
        default:
          return p_name;
      }
    } else {
      switch (p_type) {
        case '>=':
          return p_name + ' is greater than or equal to ' + p_value;
        case '>':
          return p_name + ' is greater than ' + p_value;
        case '<=':
          return p_name + ' is less than or equal to ' + p_value;
        case '<':
          return p_name + ' is less than ' + p_value;
        case '=':
          return p_name + ' is equal to ' + p_value;
        default:
          return p_name;
      }
    }
  };

  //To do: refactor for multiple credentials
  let proofAttributes;
  if (Array.isArray(proof.requested_attributes?.req_attr_0?.names)) {
    proofAttributes = proof.requested_attributes.req_attr_0.names;
  } else {
    proofAttributes = Object.keys(proof.requested_attributes);
  }

  return (
    <View style={styles.cardAttributes}>
      <View style={styles.cardAttributes}>
        <Text style={styles.cardAttributesSectionTitle}>Requested information</Text>
      </View>
      {Object.keys(proofAttributes).length > 0 && (
        <>
          {proofAttributes.map((item: string, index: React.Key) => {
            if (item.toLowerCase().includes('first_name') && proofAttributes.length === 1) {
              return (
                <View key={index} style={styles.cardAttribute}>
                  <View style={styles.attributeTitle}>
                    <Text style={styles.attributeName}>- A Valid Quartech Employee ID Card</Text>
                  </View>
                </View>
              );
            }

            let value = proof.attributes.find((obj) => obj.name === item)?.value;
            if (item.toLowerCase().includes('date') && !isNaN(Number(value))) {
              value = getDateString(Number(value));
            }
            return (
              <View key={index} style={styles.cardAttribute}>
                <View style={styles.attributeTitle}>
                  <Text style={styles.attributeName}>{mapKey(item)}</Text>
                </View>
                <View style={index !== proof.attributes.length - 1 && styles.attributeBorder}>
                  {isBase64Image(value ? value : '') ? (
                    <View style={styles.attributeImageContainer}>
                      <Image source={{ uri: value }} style={styles.attributeImage} />
                    </View>
                  ) : (
                    <Text style={styles.attributeValue}>{value}</Text>
                  )}
                </View>
              </View>
            );
          })}
        </>
      )}
      {Object.keys(proof.requested_predicates).length > 0 && (
        <>
          {Object.keys(proof.requested_predicates).map((item, index) => {
            let value = proof.attributes.find(
              (obj) => obj.name === proof.requested_predicates[item].name
            )?.value;
            const name = mapKey(proof.requested_predicates[item].name);
            const pType = proof.requested_predicates[item].p_type;
            const pValue = proof.requested_predicates[item].p_value;

            if (name.toLowerCase().includes('date') && !isNaN(Number(value))) {
              value = getDateString(Number(value));
            }
            const predicateString = createPredicateString(name, pType, pValue);
            return (
              <View key={item} style={styles.cardAttribute}>
                <View style={styles.attributeTitle}>
                  <Text style={styles.attributeName}>{predicateString}</Text>
                </View>
                <View style={index !== proof.attributes.length - 1 && styles.attributeBorder}>
                  <Text style={styles.attributeValue}>{value}</Text>
                </View>
              </View>
            );
          })}
        </>
      )}
    </View>
  );
};

export default ProofDetails;

const styles = StyleSheet.create({
  attributeName: {
    fontSize: 14,
    color: '#30394C',
    fontWeight: '400'
  },
  attributeValue: {
    fontSize: 18,
    color: '#30394C',
    fontWeight: '400'
  },
  cardAttributesSectionTitle: {
    marginBottom: 8,
    fontSize: 16,
    fontWeight: '700'
  },
  cardAttributes: {
    display: 'flex',
    gap: 1,
    borderRadius: 6,
    overflow: 'hidden'
  },
  cardAttribute: {
    paddingTop: 12,
    paddingRight: 0,
    paddingBottom: 14,
    paddingLeft: 0
  },
  attributeImageContainer: {
    marginTop: 6,
    width: 256,
    aspectRatio: '1/1',
    overflow: 'hidden',
    borderRadius: 6
  },
  attributeImage: {
    width: '100%',
    height: '100%'
  },
  attributeTitle: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  attributeBorder: {
    borderBottomWidth: 1,
    borderColor: '#CECECE',
    paddingBottom: 5
  }
});
